import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
  FormGroup,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useGetDocumentChecklistByIdQuery,
  useCreateDocumentChecklistMutation,
  useUpdateDocumentChecklistMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

interface DocumentChecklistFormProps {
  id?: number | null;
  onSaved?: () => void;
}

const DocumentChecklistForm: React.FC<DocumentChecklistFormProps> = ({
  id,
  onSaved,
}) => {
  const isEditMode = id != null;
  const [saveError, setSaveError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const { data: detail, isFetching: fetchingDetail } =
    useGetDocumentChecklistByIdQuery(id!, { skip: !isEditMode || id == null });

  const [createDoc, { isLoading: creating }] =
    useCreateDocumentChecklistMutation();
  const [updateDoc, { isLoading: updating }] =
    useUpdateDocumentChecklistMutation();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      document_name: "",
      description: "",
      // is_required: false,
      status: "active",
    },
    validationSchema: Yup.object({
      // document_name: Yup.string().required("Document name is required"),
      status: Yup.string().oneOf(
        STATUS_OPTIONS.map((o) => o.value),
        "Invalid status",
      ),
    }),
    onSubmit: async (values) => {
      setSaveError(null);
      try {
        const formData = new FormData();
        formData.append("document_name", values.document_name);
        if (values.description)
          formData.append("description", values.description);
        // formData.append("is_required", String(values.is_required));
        formData.append("status", values.status);
        if (file) formData.append("file", file);

        if (isEditMode && id != null) {
          await updateDoc({ id, body: formData }).unwrap();
        } else {
          await createDoc(formData).unwrap();
        }
        setFile(null);
        if (onSaved) onSaved();
      } catch (err: any) {
        const msg =
          err?.data?.document_name?.[0] ||
          err?.data?.detail ||
          err?.message ||
          "Failed to save";
        setSaveError(String(msg));
      }
    },
  });

  useEffect(() => {
    if (isEditMode && detail) {
      formik.setValues({
        document_name: detail.document_name ?? "",
        description: detail.description ?? "",
        // is_required: detail.is_required ?? false,
        status: detail.status ?? "active",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, isEditMode]);

  if (isEditMode && fetchingDetail) {
    return (
      <div className="text-center py-4">
        <Spinner /> Loading...
      </div>
    );
  }

  return (
    <>
      {saveError && <Alert color="danger">{saveError}</Alert>}
      <form onSubmit={formik.handleSubmit}>
        <Row>
          <Col md={12}>
            <FormGroup className="mb-3">
              <Label>
                Document Name <span className="text-danger">*</span>
              </Label>
              <Input
                name="document_name"
                placeholder="e.g. Certificate of Conformity"
                value={formik.values.document_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  !!(
                    formik.touched.document_name && formik.errors.document_name
                  )
                }
              />
              {formik.touched.document_name && formik.errors.document_name && (
                <div className="text-danger small mt-1">
                  {formik.errors.document_name}
                </div>
              )}
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup className="mb-3">
              <Label>Description</Label>
              <Input
                type="textarea"
                name="description"
                placeholder="Optional description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                rows={2}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup className="mb-3">
              <Label>Supporting file (optional)</Label>
              <Input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
              />
              {isEditMode && detail?.file_url && !file && (
                <a
                  href={detail.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="small d-block mt-1"
                >
                  View current file
                </a>
              )}
            </FormGroup>
          </Col>
          {/* <Col md={6}>
            <FormGroup className="mb-3">
              <div className="form-check form-switch">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="is_required"
                  name="is_required"
                  checked={formik.values.is_required}
                  onChange={formik.handleChange}
                />
                <Label className="form-check-label" htmlFor="is_required">
                  Required
                </Label>
              </div>
            </FormGroup>
          </Col> */}
          <Col md={6}>
            <FormGroup className="mb-3">
              <Label>Status</Label>
              <Input
                type="select"
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <div className="text-end mt-3">
          <Button color="primary" type="submit" disabled={creating || updating}>
            {creating || updating ? (
              <>
                <Spinner size="sm" className="me-1" /> Saving...
              </>
            ) : isEditMode ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </form>
    </>
  );
};

export default DocumentChecklistForm;
